import React, { useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Funzione helper per aggiungere titoli e dati a un foglio
const addDataToSheet = (sheet, data, title, startRow) => {
    // Aggiungi un titolo in grassetto
    XLSX.utils.sheet_add_aoa(sheet, [[title]], { origin: `A${startRow}` });
    sheet[`A${startRow}`].s = { font: { bold: true } }; // Stile grassetto

    // Aggiungi i dati della tabella
    XLSX.utils.sheet_add_json(sheet, data, {
        origin: `A${startRow + 2}`, // Lascia una riga vuota
        skipHeader: false,
    });
    return startRow + data.length + 4; // Ritorna la prossima riga di inizio (dati + header + titolo + 2 righe vuote)
};

const ExportButton = ({ currentFilters }) => {
    const [isExporting, setIsExporting] = useState(false);

    const exportToExcel = async () => {
        setIsExporting(true);
        try {
            const params = {
                location: currentFilters.location,
                passageCounterID: currentFilters.passageCounterID, // Questo è l'ID del punto di interesse (in_id)
                startDate: currentFilters.startDate ? Math.floor(currentFilters.startDate.setHours(0, 0, 0, 0) / 1000) : null,
                endDate: currentFilters.endDate ? Math.floor(currentFilters.endDate.setHours(23, 59, 59, 999) / 1000) : null,
            };
            Object.keys(params).forEach(key => {
                const value = params[key];
                if (value === null || value === '' || (typeof value === 'number' && isNaN(value))) {
                    delete params[key];
                }
            });

            const { data } = await axios.get(
                `/api/backoffice/passages/export`,
                {
                    params: params,
                    withCredentials: true
                }
            );

            // 1. Foglio "Passaggi"
            // ELABORAZIONE: Aggiunta colonna "Mese"
            let processedData = data.rawData.map(row => {
                const dateString = row['Data e ora'];
                let nomeMese = '';

                // Calcolo del mese
                try {
                    const dateObj = new Date(dateString);
                    if (!isNaN(dateObj.getTime())) {
                        nomeMese = dateObj.toLocaleString('it-IT', { month: 'long' });
                        nomeMese = nomeMese.charAt(0).toUpperCase() + nomeMese.slice(1);
                    }
                } catch (e) {
                    console.warn("Errore parsing data:", row);
                }

                return {
                    'ID passaggio': row['ID passaggio'],
                    'Data e ora': row['Data e ora'],
                    'Mese': nomeMese,
                    'Direzione': row['Direzione'],
                    'Nome contapassaggi': row['Nome contapassaggi'],
                    'Nome punto di interesse collegato': row['Nome punto di interesse collegato'],
                    'Parco': row['Parco']
                };
            });

            // ORDINAMENTO: Ordina per data decrescente (dall'ultimo al primo)
            processedData.sort((a, b) => {
                // Converte le stringhe in oggetti Date per il confronto
                const dateA = new Date(a['Data e ora']);
                const dateB = new Date(b['Data e ora']);
                // b - a produce un ordinamento decrescente
                return dateB - dateA;
            });

            // Creazione del foglio con i dati ordinati
            const ws1 = XLSX.utils.json_to_sheet(processedData);

            // *** AGGIUNGE I FILTRI TABELLA DI EXCEL ***
            const range = XLSX.utils.decode_range(ws1['!ref']);
            range.e.c = 6; // Aggiornato per includere la nuova colonna (indice 6 = 7 colonne)
            ws1['!autofilter'] = { ref: XLSX.utils.encode_range(range) };

            // Larghezza colonne (inclusa la nuova colonna Mese)
            ws1['!cols'] = [
                { wch: 10 }, // ID
                { wch: 20 }, // Data e ora
                { wch: 15 }, // Mese
                { wch: 10 }, // Direzione
                { wch: 25 }, // Contapassaggi
                { wch: 30 }, // Punto interesse
                { wch: 20 }  // Parco
            ];

            // 2. Foglio "Statistiche" (ex "Grafico")
            const ws2 = XLSX.utils.book_new();
            let currentRow = 1;

            // Aggiunge la prima tabella (Statistiche per Punto di Interesse)
            currentRow = addDataToSheet(ws2, data.byCounter, "Riepilogo per Punto di Interesse", currentRow);

            // Aggiunge la seconda tabella (Statistiche per Giorno)
            addDataToSheet(ws2, data.byDate, "Riepilogo per Giorno", currentRow);

            // Larghezza colonne per "Statistiche"
            ws2['!cols'] = [{ wch: 25 }, { wch: 20 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }];

            // 3. Foglio "Legenda"
            const legenda = [
                { "Info": "Legenda Parchi" },
                { "Sigla": "AC", "Parco": "Alpi Cozie" },
                { "Sigla": "GP", "Parco": "Gran Paradiso" },
            ];
            const ws3 = XLSX.utils.json_to_sheet(legenda);
            ws3['!cols'] = [{ wch: 10 }, { wch: 20 }];

            // Assemblaggio file
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws1, "Passaggi");
            XLSX.utils.book_append_sheet(wb, ws2, "Statistiche");
            XLSX.utils.book_append_sheet(wb, ws3, "Legenda");

            const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
            saveAs(blob, 'report_passaggi.xlsx');

        } catch (err) {
            console.error("Errore durante l'esportazione Excel", err);
            if (err.response && err.response.status === 401) {
                alert("Errore di autenticazione. Effettua nuovamente il login.");
            } else {
                alert("Si è verificato un errore durante l'esportazione.");
            }
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <button
            onClick={exportToExcel}
            disabled={isExporting}
            // Usa le classi CSS globali definite in index.css
            className="button button-secondary"
            style={{ alignSelf: 'flex-end' }} // Allinea con gli altri bottoni
        >
            {isExporting ? 'Generando...' : 'Esporta Excel'}
        </button>
    );
};

export default ExportButton;